/**
 * What can go wrong reading or writing an export file.
 *
 * Deliberately not the RCU configuration's issue catalog and not the keypad
 * feature's: a file that cannot be read is neither a wiring problem nor a panel
 * problem, and reporting one as the other would send a technician to the wrong
 * tab. The copy here is the console's own words for a file it will not use.
 */

export const EXPORT_SEVERITY = {
  /** Nothing is applied while this stands. */
  BLOCKING: 'blocking',
  /** The file was read; something in it is worth knowing. */
  ADVISORY: 'advisory',
} as const;

export type ExportSeverity = (typeof EXPORT_SEVERITY)[keyof typeof EXPORT_SEVERITY];

export const EXPORT_ISSUE = {
  /** The file had no content at all. */
  EMPTY_FILE: 'empty_file',
  /** The file is larger than anything this configuration could be. */
  FILE_TOO_LARGE: 'file_too_large',
  /** Quotes, commas or newlines the reader could not make sense of. */
  MALFORMED_CSV: 'malformed_csv',
  /** No `meta` row, or a format marker that is not ours. */
  UNKNOWN_FORMAT: 'unknown_format',
  /** Our file, laid out by a version this build does not know. */
  VERSION_UNSUPPORTED: 'version_unsupported',
  /** A row whose first cell names no section of this format. */
  UNKNOWN_SECTION: 'unknown_section',
  /** A section header that does not name the fields this build expects. */
  SECTION_MISMATCH: 'section_mismatch',
  /** A data row whose field count does not match its header. */
  ROW_SHAPE: 'row_shape',
  /** A section holds more rows than any real installation could. */
  TOO_MANY_ROWS: 'too_many_rows',
  /** No `rcu` row: a file with no device in it is not a configuration. */
  NO_RCU: 'no_rcu',
  /** The rows could not be read as a configuration this build understands. */
  CONFIG_UNREADABLE: 'config_unreadable',
  /** The rows read, but the configuration breaks a rule the console enforces. */
  CONFIG_INVALID: 'config_invalid',
  /** The rows could not be read as a keypad book this build understands. */
  KEYPADS_UNREADABLE: 'keypads_unreadable',
  /** The file read, but the browser would not hand it over. */
  FILE_UNREADABLE: 'file_unreadable',
  /** The browser refused to save the file. */
  SAVE_FAILED: 'save_failed',
} as const;

export type ExportIssueCode = (typeof EXPORT_ISSUE)[keyof typeof EXPORT_ISSUE];

export interface ExportIssue {
  readonly code: ExportIssueCode;
  readonly severity: ExportSeverity;
  readonly message: string;
}

/** Nothing is applied while this stands. */
export const blocking = (code: ExportIssueCode, message: string): ExportIssue => ({
  code,
  severity: EXPORT_SEVERITY.BLOCKING,
  message,
});

/** Something worth telling the technician, which does not stop the import. */
export const advisory = (code: ExportIssueCode, message: string): ExportIssue => ({
  code,
  severity: EXPORT_SEVERITY.ADVISORY,
  message,
});

/** True when this issue means nothing may be applied. */
export const isBlocking = (issue: ExportIssue): boolean =>
  issue.severity === EXPORT_SEVERITY.BLOCKING;

/**
 * The single place this catalog's copy is written.
 *
 * Each message takes the caller's own specifics — the section name, the row
 * number, the offending value — because a refusal a technician cannot act on is
 * only slightly better than no refusal at all.
 */
const MESSAGE = {
  [EXPORT_ISSUE.EMPTY_FILE]: () => 'That file is empty.',
  [EXPORT_ISSUE.FILE_TOO_LARGE]: (detail) =>
    `That file is ${detail}, larger than this console reads.`,
  [EXPORT_ISSUE.MALFORMED_CSV]: (detail) => `That file is not readable as CSV: ${detail}.`,
  [EXPORT_ISSUE.UNKNOWN_FORMAT]: () =>
    'That CSV was not written by this console, so its columns are not known here.',
  [EXPORT_ISSUE.VERSION_UNSUPPORTED]: (detail) =>
    `That file was written by export version ${detail}, which this build does not read.`,
  [EXPORT_ISSUE.UNKNOWN_SECTION]: (detail) =>
    `That file has a row this console does not recognise: ${detail}.`,
  [EXPORT_ISSUE.SECTION_MISMATCH]: (detail) =>
    `A section has no header row this build recognises: ${detail}. Its columns are unknown.`,
  [EXPORT_ISSUE.ROW_SHAPE]: (detail) => `A row has the wrong number of fields: ${detail}.`,
  [EXPORT_ISSUE.TOO_MANY_ROWS]: (detail) =>
    `The ${detail} section holds more rows than any real installation.`,
  [EXPORT_ISSUE.NO_RCU]: () =>
    'That file has no RCU row, so it does not describe a controller.',
  [EXPORT_ISSUE.CONFIG_UNREADABLE]: () =>
    'The rows in that file could not be read as a configuration.',
  [EXPORT_ISSUE.CONFIG_INVALID]: (detail) => `That configuration cannot be applied: ${detail}`,
  [EXPORT_ISSUE.KEYPADS_UNREADABLE]: () => 'The keypad rows in that file could not be read.',
  [EXPORT_ISSUE.FILE_UNREADABLE]: (detail) => `That file could not be read: ${detail}.`,
  [EXPORT_ISSUE.SAVE_FAILED]: (detail) => `The file could not be saved: ${detail}.`,
} as const satisfies Record<ExportIssueCode, (detail: string) => string>;

/** Builds a blocking issue from the catalog's own copy. */
export const issueFor = (code: ExportIssueCode, detail = ''): ExportIssue =>
  blocking(code, MESSAGE[code](detail));

/** Builds an advisory from the catalog's own copy. */
export const advisoryFor = (code: ExportIssueCode, detail = ''): ExportIssue =>
  advisory(code, MESSAGE[code](detail));

/** The message an issue carries, which is always already written out. */
export const exportMessage = (issue: ExportIssue): string => issue.message;
